import type { TileWrite } from './types';

interface WaterNeighbors {
  readonly n: boolean;
  readonly s: boolean;
  readonly e: boolean;
  readonly w: boolean;
}

export class WaterAutoTile {
  apply(writes: readonly TileWrite[]): readonly TileWrite[] {
    const river = new Set(writes.filter((write) => write.type === 'water' && write.variant?.startsWith('river_')).map(key));
    const lake = new Set(writes.filter((write) => write.type === 'water' && !write.variant?.startsWith('river_')).map(key));

    return writes.map((write) => {
      if (write.type !== 'water') return write;
      if (write.variant === 'bridge_h' || write.variant === 'bridge_v') return write;

      const source = write.variant?.startsWith('river_') ? river : lake;
      const neighbors = {
        n: source.has(`${write.x}:${write.y - 1}`),
        s: source.has(`${write.x}:${write.y + 1}`),
        e: source.has(`${write.x + 1}:${write.y}`),
        w: source.has(`${write.x - 1}:${write.y}`),
      };

      if (write.variant?.startsWith('river_')) return { ...write, variant: this.riverVariant(write.variant, neighbors) };
      return { ...write, variant: this.lakeVariant(write.x, write.y, neighbors) };
    });
  }

  private lakeVariant(x: number, y: number, neighbors: WaterNeighbors): string {
    if (neighbors.n && neighbors.s && neighbors.e && neighbors.w) {
      return (x + y) % 2 === 0 ? 'water27/sea_deep_1' : 'water27/sea_deep_2';
    }

    if (!neighbors.n && !neighbors.w) return 'water27/shore_out_nw';
    if (!neighbors.n && !neighbors.e) return 'water27/shore_out_ne';
    if (!neighbors.s && !neighbors.w) return 'water27/shore_out_sw';
    if (!neighbors.s && !neighbors.e) return 'water27/shore_out_se';

    if (!neighbors.n) return 'water27/shore_north';
    if (!neighbors.s) return 'water27/shore_south';
    if (!neighbors.e) return 'water27/shore_east';
    if (!neighbors.w) return 'water27/shore_west';

    return 'water27/shallow_1';
  }

  private riverVariant(sourceVariant: string, neighbors: WaterNeighbors): string {
    if (sourceVariant === 'river_wide') return 'water27/shallow_1';
    if (sourceVariant === 'river_h') return 'water27/river_h';
    if (sourceVariant === 'river_v') return 'water27/river_v';
    if (sourceVariant === 'river_end') return 'water27/river_source';
    if (sourceVariant === 'river_e_n') return 'water27/river_bend_ne';
    if (sourceVariant === 'river_n_w') return 'water27/river_bend_nw';
    if (sourceVariant === 'river_e_s') return 'water27/river_bend_se';
    if (sourceVariant === 'river_s_w') return 'water27/river_bend_sw';

    const count = Number(neighbors.n) + Number(neighbors.s) + Number(neighbors.e) + Number(neighbors.w);
    if (count >= 4) return 'water27/river_cross';
    if (count === 3) {
      if (!neighbors.n) return 'water27/river_t_n';
      if (!neighbors.s) return 'water27/river_t_s';
      if (!neighbors.e) return 'water27/river_t_e';
      return 'water27/river_t_w';
    }
    if (count === 2 && neighbors.n && neighbors.e) return 'water27/river_bend_ne';
    if (count === 2 && neighbors.n && neighbors.w) return 'water27/river_bend_nw';
    if (count === 2 && neighbors.s && neighbors.e) return 'water27/river_bend_se';
    if (count === 2 && neighbors.s && neighbors.w) return 'water27/river_bend_sw';
    if (neighbors.n && neighbors.s) return 'water27/river_v';
    if (neighbors.e && neighbors.w) return 'water27/river_h';
    if (neighbors.e || neighbors.w) return 'water27/river_h';
    if (neighbors.n || neighbors.s) return 'water27/river_v';

    return sourceVariant === 'river_end' ? 'water27/river_source' : 'water27/shallow_1';
  }
}

function key(write: Pick<TileWrite, 'x' | 'y'>): string {
  return `${write.x}:${write.y}`;
}
